import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Typography
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { Success } from "../../../../commons/Messages/Messages";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import MoneyInput from "../shared/MoneyInput";
import { formatCents } from "../shared/money";
import {
  createSelfBilling,
  getBeleg,
  issueSelfBilling,
  listSuppliers,
  updateSelfBilling
} from "./api";

const RATES = [19, 7, 0];
const EMPTY_LINE = { description: "", quantity: 1, unit: "", unitPrice: 0, rate: 19 };

const lineNet = l => Math.round(Number(l?.quantity || 0) * Number(l?.unitPrice || 0));

const toForm = beleg => ({
  supplier: beleg.supplier?._id || beleg.supplier,
  invoiceDate: beleg.invoiceDate ? dayjs(beleg.invoiceDate) : dayjs(),
  deliveryDate: beleg.deliveryDate ? dayjs(beleg.deliveryDate) : null,
  servicePeriod: beleg.selfBilling?.servicePeriod || "",
  reverseCharge: Boolean(beleg.selfBilling?.reverseCharge),
  purpose: beleg.purpose || "",
  lines: (beleg.items || []).map(i => ({
    description: i.description,
    quantity: i.quantity,
    unit: i.unit,
    unitPrice: i.unitPrice,
    rate: i.taxRate
  }))
});

const fromForm = values => ({
  supplier: values.supplier,
  invoiceDate: values.invoiceDate ? values.invoiceDate.format("YYYY-MM-DD") : "",
  deliveryDate: values.deliveryDate ? values.deliveryDate.format("YYYY-MM-DD") : "",
  servicePeriod: values.servicePeriod || "",
  reverseCharge: Boolean(values.reverseCharge),
  purpose: values.purpose || "",
  lines: (values.lines || []).map(l => ({
    description: l.description || "",
    quantity: Number(l.quantity) || 1,
    unit: l.unit || "",
    unitPrice: l.unitPrice || 0,
    net: lineNet(l),
    rate: values.reverseCharge ? 19 : l.rate
  }))
});

/**
 * Gutschrift im Gutschriftverfahren (§ 14 Abs. 2 S. 2 UStG): we write the invoice for a
 * subcontractor who agreed to it. Save as draft, or issue: number GSV-…, PDF, booking.
 * `supplier` preselects the subcontractor; `belegId` opens an existing draft.
 */
const SelfBillingModal = ({ open, supplier, belegId, onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [missing, setMissing] = useState([]);
  const lines = Form.useWatch("lines", form) || [];
  const reverseCharge = Form.useWatch("reverseCharge", form);
  const supplierId = Form.useWatch("supplier", form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setMissing([]);
    setDraftId(belegId || null);
    listSuppliers({ limit: 500 })
      .then(res =>
        setSuppliers((res.data.suppliers || []).filter(s => s.selfBillingAgreement?.agreed))
      )
      .catch(() => {});
    if (belegId) {
      setLoading(true);
      getBeleg(belegId)
        .then(res => form.setFieldsValue(toForm(res.data.beleg)))
        .catch(err => {
          generalErrorAlert(err);
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      form.setFieldsValue({
        supplier: supplier?._id,
        invoiceDate: dayjs(),
        reverseCharge: Boolean(supplier?.is13bSupplier),
        lines: [{ ...EMPTY_LINE }]
      });
    }
  }, [open, belegId, supplier]);

  const chosen = useMemo(
    () => suppliers.find(s => s._id === supplierId) || (supplier?._id === supplierId && supplier),
    [suppliers, supplierId, supplier]
  );

  const totals = useMemo(() => {
    const byRate = new Map();
    for (const l of lines) {
      const rate = reverseCharge ? 19 : Number(l?.rate ?? 19);
      byRate.set(rate, (byRate.get(rate) || 0) + lineNet(l));
    }
    let net = 0;
    let tax = 0;
    for (const [rate, n] of byRate) {
      net += n;
      tax += reverseCharge ? 0 : Math.round((n * rate) / 100);
    }
    return { net, tax, gross: net + tax };
  }, [lines, reverseCharge]);

  /** Creates or updates the draft; returns its id. */
  const saveDraft = async () => {
    const values = await form.validateFields();
    const body = fromForm(values);
    if (draftId) {
      await updateSelfBilling(draftId, body);
      return draftId;
    }
    const res = await createSelfBilling(body);
    setDraftId(res.data.beleg._id);
    return res.data.beleg._id;
  };

  const onSave = async () => {
    setSaving("save");
    try {
      await saveDraft();
      Success(t("Draft saved"));
      onDone?.();
    } catch (err) {
      if (!err?.errorFields) generalErrorAlert(err);
    } finally {
      setSaving("");
    }
  };

  const onIssue = async () => {
    setSaving("issue");
    setMissing([]);
    try {
      const id = await saveDraft();
      const res = await issueSelfBilling(id);
      Success(
        t("Gutschrift {{number}} issued and booked", { number: res.data.beleg.invoiceNumber })
      );
      onDone?.(res.data.beleg);
    } catch (err) {
      if (err?.errorFields) return;
      const details = err?.response?.data?.details;
      if (details?.missing) setMissing(details.missing);
      generalErrorAlert(err);
    } finally {
      setSaving("");
    }
  };

  const MISSING = {
    name: t("Name"),
    street: t("Street"),
    city: t("ZIP and city"),
    taxNumberOrVatId: t("Tax number or VAT ID")
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("Create Gutschrift (self-billing)")}
      width={900}
      rootClassName="acct-module"
      destroyOnClose
      footer={
        <Space wrap style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button onClick={onSave} loading={saving === "save"} disabled={loading}>
            {t("Save draft")}
          </Button>
          <Popconfirm
            title={t("Issue and book this Gutschrift?")}
            description={t(
              "It gets its final number and can then only be reversed by an objection."
            )}
            onConfirm={onIssue}
          >
            <Button type="primary" loading={saving === "issue"} disabled={loading}>
              {t("Issue Gutschrift")}
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Form form={form} layout="vertical" size={isMobile ? "middle" : "small"}>
          <Alert
            type="info"
            showIcon
            className="mb-3"
            message={t(
              'You bill yourself on behalf of the subcontractor. The document is titled "Gutschrift" and carries the subcontractor\'s tax number; they may object to it.'
            )}
          />
          {missing.length > 0 && (
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={t("Complete the subcontractor's master data first")}
              description={missing.map(key => MISSING[key] || key).join(", ")}
            />
          )}
          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Form.Item
                name="supplier"
                label={t("Subcontractor")}
                rules={[{ required: true, message: t("Required") }]}
                extra={
                  suppliers.length === 0
                    ? t("Only suppliers with a self-billing agreement can be chosen.")
                    : chosen?.selfBillingAgreement?.date
                      ? t("Agreement of {{date}}", {
                          date: dayjs(chosen.selfBillingAgreement.date).format("DD.MM.YYYY")
                        })
                      : null
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={suppliers.map(s => ({
                    value: s._id,
                    label: `${s.name} (${s.supplierNumber})`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="invoiceDate"
                label={t("Issue date")}
                rules={[{ required: true, message: t("Required") }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="deliveryDate" label={t("Delivery / service date")}>
                <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Form.Item name="servicePeriod" label={t("Service period (instead of a date)")}>
                <Input placeholder={t("e.g. April 2026")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="reverseCharge"
                label={t("Reverse charge (§ 13b UStG)")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }, index) => {
                  const labelled = index === 0 || isMobile;
                  return (
                    <Row gutter={8} key={key} align="middle">
                      <Col xs={24} md={9}>
                        <Form.Item
                          name={[name, "description"]}
                          label={labelled ? t("Service") : ""}
                          rules={[{ required: true, message: t("Required") }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={8} md={3}>
                        <Form.Item name={[name, "quantity"]} label={labelled ? t("Quantity") : ""}>
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={6} md={2}>
                        <Form.Item name={[name, "unit"]} label={labelled ? t("Unit") : ""}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={10} md={4}>
                        <Form.Item
                          name={[name, "unitPrice"]}
                          label={labelled ? t("Unit price (net)") : ""}
                        >
                          <MoneyInput />
                        </Form.Item>
                      </Col>
                      <Col xs={8} md={2}>
                        <Form.Item name={[name, "rate"]} label={labelled ? t("Tax rate") : ""}>
                          <Select
                            disabled={reverseCharge}
                            options={RATES.map(r => ({ value: r, label: `${r} %` }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Item label={labelled ? t("Net") : ""}>
                          <span className="acct-num">{formatCents(lineNet(lines[index]))}</span>
                        </Form.Item>
                      </Col>
                      <Col xs={4} md={1}>
                        <Form.Item label={labelled ? " " : ""}>
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              icon={<MinusCircleOutlined />}
                              aria-label={t("Delete")}
                              onClick={() => remove(name)}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                })}
                <Space wrap style={{ marginBottom: 12 }}>
                  <Button icon={<PlusOutlined />} onClick={() => add({ ...EMPTY_LINE })}>
                    {t("Add line")}
                  </Button>
                  <Typography.Text strong>
                    {t("Net")}: <span className="acct-num">{formatCents(totals.net)}</span>
                    {" · "}
                    {t("VAT")}: <span className="acct-num">{formatCents(totals.tax)}</span>
                    {" · "}
                    {t("Total")}: <span className="acct-num">{formatCents(totals.gross)}</span>
                  </Typography.Text>
                </Space>
              </>
            )}
          </Form.List>
          {reverseCharge && (
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message={t(
                "No VAT on the document; you owe the VAT and deduct it as input tax (§ 13b UStG)."
              )}
            />
          )}
          <Form.Item name="purpose" label={t("Note on the document")}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default SelfBillingModal;
